import React, { Fragment, useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import CheckboxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';

// Components
import { ProductIndicatorBadge } from 'common';

// Utils
import { arrayOf, bool, func, shape, string } from 'prop-types';
import * as R from 'ramda';

const SelectProducts = ({
  allEnabled,
  enabledOptions,
  header,
  inactiveListOptions,
  setEnabledOptions,
  setHasChangesToStepTwo,
  toggleAllMessage,
}) => {
  const [startingState] = useState(enabledOptions);

  useEffect(() => {
    if (!R.isNil(setHasChangesToStepTwo)) {
      if (!R.equals(startingState, enabledOptions)) setHasChangesToStepTwo(true);
      else setHasChangesToStepTwo(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledOptions]);

  const handleSingleOption = (id, selected) => setEnabledOptions({ id, selected });

  const handleToggleAll = (__, toggle) =>
    R.forEach(({ value }) => handleSingleOption(value, toggle), inactiveListOptions);

  const message = () => (
    <Typography color="textPrimary" component="div" variant="body1">
      <Box pb={1.5} pt="14px">
        {inactiveListOptions.length ? toggleAllMessage : 'No Options Available'}
      </Box>
    </Typography>
  );

  return (
    <>
      <Box
        pb={3}
        px={3}
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.graphic.light}`,
        }}
      >
        <Typography color="textPrimary" variant="body1">
          {header}
        </Typography>
      </Box>
      <Box
        px={3}
        py="5.5px"
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.graphic.light}`,
        }}
      >
        {inactiveListOptions.length ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={allEnabled}
                checkedIcon={<CheckboxRoundedIcon sx={{ color: 'secondary.main' }} />}
                icon={<CheckBoxOutlineBlankRoundedIcon />}
                name="selectAll"
                onChange={handleToggleAll}
                sx={{
                  '&.MuiCheckbox-root': {
                    borderRadius: '50%',
                    color: 'text.secondary',
                  },
                }}
              />
            }
            label={message()}
          />
        ) : (
          message()
        )}
      </Box>
      {inactiveListOptions.map(({ value, label, proprietary, selected }) => (
        <Fragment key={value}>
          <Box
            alignItems="center"
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            px={3}
            py="7.5px"
            sx={{
              borderBottom: '1px solid',
              borderBottomColor: 'graphic.light',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={selected}
                  checkedIcon={<CheckboxRoundedIcon sx={{ color: 'secondary.main' }} />}
                  icon={<CheckBoxOutlineBlankRoundedIcon />}
                  name={`${value}`}
                  sx={{
                    '&.MuiCheckbox-root': {
                      borderRadius: '50%',
                    },
                  }}
                />
              }
              label={
                <Typography color="textPrimary" sx={{ pt: '2px' }} variant="body1">
                  {label}
                </Typography>
              }
              onChange={() => handleSingleOption(value, !selected)}
              sx={{ color: 'text.secondary' }}
            />

            <ProductIndicatorBadge proprietary={proprietary} />
          </Box>
        </Fragment>
      ))}
    </>
  );
};

SelectProducts.propTypes = {
  allEnabled: bool.isRequired,
  enabledOptions: arrayOf(shape),
  header: string.isRequired,
  inactiveListOptions: arrayOf(shape).isRequired,
  setEnabledOptions: func.isRequired,
  setHasChangesToStepTwo: func,
  toggleAllMessage: string.isRequired,
};

SelectProducts.defaultProps = {
  enabledOptions: [],
  setHasChangesToStepTwo: undefined,
};

export default SelectProducts;
